/**
 * Parses a numeric `order` value from a product's `productTags` array.
 *
 * Backend ships an "order" tag on standard/premium offer products to
 * pin a specific display order independent of price. The tag is matched
 * case-insensitively and accepts the common CMS shapes:
 *   "ORDER:1" | "order=2" | "order_3" | "order-4" | "ORDER 5" | "order6"
 *
 * Returns the first matching numeric value, or undefined when no tag
 * is present so the caller can fall back to a secondary sort key.
 */
export function extractOrderFromTags(productTags: string[] | undefined | null): number | undefined {
  if (!productTags || productTags.length === 0) return undefined;
  const orderRegex = /^order[\s:=_-]*(\d+)$/i;
  for (const tag of productTags) {
    if (!tag) continue;
    const match = tag.trim().match(orderRegex);
    if (match) {
      const value = parseInt(match[1], 10);
      if (!Number.isNaN(value)) return value;
    }
  }
  return undefined;
}

/**
 * Comparator for offer products. Pins explicitly-ordered products first
 * (ascending by `order` tag), then sorts the rest by price ascending.
 *
 * Used by Search and Change-Date offer builders so a CMS-curated display
 * order survives the per-price sort that previously ran unconditionally.
 */
export function compareByOrderThenPrice(
  a: { productTags?: string[]; price?: number },
  b: { productTags?: string[]; price?: number },
): number {
  const orderA = extractOrderFromTags(a.productTags);
  const orderB = extractOrderFromTags(b.productTags);
  if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
  if (orderA !== undefined) return -1;
  if (orderB !== undefined) return 1;
  return (a.price ?? 0) - (b.price ?? 0);
}

/**
 * Returns the cheapest available product from a list, scanning by an
 * explicit price extractor rather than by array index.
 *
 * Necessary because {@link compareByOrderThenPrice} reorders the array by
 * CMS `order` tag, so `products[0]` is no longer guaranteed to be the
 * cheapest. Anywhere downstream that needs the "cheapest available card"
 * (train-list summary price, auto-selected default offer, differential
 * pricing anchor) must derive it from the prices themselves.
 *
 * Returns `undefined` when no product passes `isAvailable` (all sold out
 * or empty input), letting callers fall through to legacy fallbacks.
 *
 * Stable on ties: the FIRST product matching the minimum price wins.
 */
export function findCheapestAvailable<T>(
  products: T[] | null | undefined,
  isAvailable: (product: T) => boolean,
  extractPrice: (product: T) => number,
): T | undefined {
  if (!products || products.length === 0) return undefined;
  let cheapest: T | undefined;
  let cheapestPrice = Infinity;
  for (const product of products) {
    if (!isAvailable(product)) continue;
    const price = extractPrice(product);
    if (price < cheapestPrice) {
      cheapest = product;
      cheapestPrice = price;
    }
  }
  return cheapest;
}
